export type ClientMsg =
  | { type: 'user'; prompt: string; snapshot: string; history: Array<{ role: 'user' | 'assistant'; content: string }> }
  | { type: 'tool_result'; id: string; result: unknown }
  // Start a fresh conversation: the proxy recreates its per-connection agent,
  // dropping all accumulated Strands history. No reconnect / re-auth.
  | { type: 'reset' }
  // Request a short-lived pairing code for MCP session linking.
  | { type: 'mint_pair_code' };

export type ServerMsg =
  | { type: 'text'; delta: string }
  | { type: 'tool_use'; id: string; name: string; input: Record<string, unknown> }
  | { type: 'step'; label: string }
  | { type: 'done' }
  | { type: 'error'; message: string }
  // Response to mint_pair_code: the code and its expiry timestamp (ms since epoch).
  | { type: 'pair_code'; code: string; expiresAt: number };

export interface AgentProvider {
  open(): Promise<void>;
  send(msg: ClientMsg): void;
  onMessage(cb: (m: ServerMsg) => void | Promise<void>): void;
  /** Optional: invoked when the socket closes (e.g. dropped connection). */
  onClose?(cb: () => void): void;
  /** Reset the proxy's agent memory (new conversation). No-op if not open. */
  reset(): void;
  close(): void;
}

export function createWsProvider(url: string): AgentProvider {
  let ws: WebSocket | null = null;
  let cb: (m: ServerMsg) => void | Promise<void> = () => {};
  let closeCb: (() => void) | null = null;
  return {
    async open() {
      // Reuse a live socket across turns (proxy keeps per-connection history,
      // so reuse also gives the agent multi-turn memory). Reconnect otherwise.
      if (ws && ws.readyState === WebSocket.OPEN) return;
      ws = new WebSocket(url);
      await new Promise<void>((resolve, reject) => {
        ws!.onopen = () => resolve();
        ws!.onerror = () => reject(new Error(`failed to open ${url}`));
      });
      ws.onmessage = ev => { try { cb(JSON.parse(String(ev.data)) as ServerMsg); } catch { /* ignore */ } };
      ws.onclose = () => closeCb?.();
    },
    send(msg) { ws?.send(JSON.stringify(msg)); },
    onMessage(handler) { cb = handler; },
    onClose(handler) { closeCb = handler; },
    reset() { if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'reset' } satisfies ClientMsg)); },
    close() { ws?.close(); ws = null; },
  };
}
